import { useQuery } from '@tanstack/react-query';
import { createCTOneIDClient, encodeSignInUI } from '@ct/oneid';
import { useCTUser } from '@ct/oneid/react';
import { env } from '../env.ts';

/**
 * Auth layer — CT One ID for identity, the atlas `users.role` for authorization.
 *
 * One ID is identity-only (who you are), reached over a same-origin session
 * cookie shared across the `ct839.*` apex. The atlas owns roles: `GET /api/me`
 * returns the caller's role, provisioning a `public` row on first sign-in.
 * There is no bearer token — requests carry the cookie via `credentials`.
 */
export const APP_ROLES = ['public', 'submitter', 'admin', 'super_admin'] as const;
export type AppRole = (typeof APP_ROLES)[number];

/** Returns true if the role is admin or super_admin. */
export function isAdminRole(role: AppRole): boolean {
  return role === 'admin' || role === 'super_admin';
}

// ─────────────────────────── hosted sign-in / out ───────────────────────────

/**
 * Cosmetic branding for One ID's hosted sign-in page (the `ct_ui` param).
 * One ID re-validates and length-caps every field, so a forged value only
 * restyles the forger's own view. Encoded once at module load.
 */
const SIGN_IN_UI = encodeSignInUI({
  // Dark neutral to match the atlas's charcoal theme (paper|slate|navy|white).
  palette: 'slate',
  hideDevicePairing: true,
  heading: 'Sign in to VEX Teams Atlas',
  subtext: 'Sign in to submit a team story or manage the atlas.',
  brandHeading: 'Explore where VEX teams come from.',
  brandSubtext:
    'A global interactive atlas of VEX Robotics teams, stories, and championship journeys.',
});

/**
 * Send the visitor to One ID's hosted sign-in page, returning them to
 * `returnTo` (must be absolute) when done. This is the only sign-in surface —
 * the atlas hosts no auth form.
 */
export function redirectToHostedSignIn(returnTo: string): void {
  window.location.href = `${env.VITE_ONEID_BASE_URL}/sign-in?returnTo=${encodeURIComponent(
    returnTo,
  )}&ct_ui=${SIGN_IN_UI}`;
}

/**
 * Sign out of One ID (clears the shared `.ct839.*` cookie → signs out of every
 * CT app, the expected SSO behavior), then return to the atlas home.
 */
export async function signOutOneId(): Promise<void> {
  const client = createCTOneIDClient({ baseURL: env.VITE_ONEID_BASE_URL });
  await client.signOut().catch(() => undefined);
  window.location.href = '/';
}

// ─────────────────────────── role (atlas /api/me) ───────────────────────────

interface MeResponse {
  ctUserId: string;
  email: string | null;
  role: AppRole;
}

/**
 * Combined sign-in + role hook.
 *
 * `useCTUser()` resolves the One ID session (identity + signed-in state). When
 * signed in, we fetch the atlas role from `/api/me` (shares the TanStack cache
 * key `['me']`). A 403 means signed-in-but-blocked → treated as not-authorized
 * (role stays `public`), never a redirect loop.
 */
export function useAppRole(): { role: AppRole; isLoaded: boolean; isSignedIn: boolean } {
  const { user, isLoading } = useCTUser();
  const me = useQuery<MeResponse>({
    queryKey: ['me'],
    queryFn: () => apiFetch<MeResponse>('/api/me'),
    enabled: !!user,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const role: AppRole = me.data?.role ?? 'public';
  const isLoaded = !isLoading && (!user || !me.isLoading);
  return { role, isLoaded, isSignedIn: !!user };
}

// ─────────────────────────────── fetch wrapper ──────────────────────────────

interface ApiFetchInit extends Omit<RequestInit, 'body'> {
  /** Object body — JSON-stringified automatically. Pass a string/FormData via `rawBody`. */
  json?: unknown;
  /** Raw body that will be passed straight through (FormData, Blob, string, etc.). */
  rawBody?: BodyInit;
  /** Public endpoint — omit the session cookie (default sends it via credentials). */
  anonymous?: boolean;
}

/** Shape of the `{ error: { code, message } }` envelope thrown by the Hono API. */
export class ApiFetchError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(opts: { status: number; code: string; message: string; details?: unknown }) {
    super(opts.message);
    this.name = 'ApiFetchError';
    this.status = opts.status;
    this.code = opts.code;
    this.details = opts.details;
  }
}

function resolveUrl(input: string): string {
  if (/^https?:\/\//i.test(input)) return input;
  const base = env.VITE_API_BASE_URL.replace(/\/+$/, '');
  return `${base}${input.startsWith('/') ? input : `/${input}`}`;
}

/**
 * Typed fetch wrapper. Auth is the same-origin One ID session cookie, sent via
 * `credentials: 'include'` (the API's CORS echoes the atlas origin + allows
 * credentials). `anonymous: true` omits the cookie for purely public reads.
 * Converts non-2xx responses to {@link ApiFetchError}.
 */
export async function apiFetch<T>(input: string, init: ApiFetchInit = {}): Promise<T> {
  const { json, rawBody, anonymous, headers: initHeaders, ...rest } = init;
  const headers = new Headers(initHeaders);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  let body: BodyInit | undefined;
  if (json !== undefined) {
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    body = JSON.stringify(json);
  } else if (rawBody !== undefined) {
    body = rawBody;
  }

  const res = await fetch(resolveUrl(input), {
    ...rest,
    headers,
    body,
    credentials: anonymous ? 'omit' : 'include',
  });
  if (!res.ok) {
    let payload: unknown;
    try {
      payload = await res.json();
    } catch {
      throw new ApiFetchError({
        status: res.status,
        code: 'http_error',
        message: `Request failed (${res.status})`,
      });
    }
    if (
      payload &&
      typeof payload === 'object' &&
      'error' in payload &&
      payload.error &&
      typeof payload.error === 'object'
    ) {
      const err = payload.error as { code?: unknown; message?: unknown; details?: unknown };
      throw new ApiFetchError({
        status: res.status,
        code: typeof err.code === 'string' ? err.code : 'http_error',
        message:
          typeof err.message === 'string' ? err.message : `Request failed (${res.status})`,
        details: err.details,
      });
    }
    throw new ApiFetchError({
      status: res.status,
      code: 'http_error',
      message: `Request failed (${res.status})`,
    });
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
